import { DocumentationArticle } from '../components/DocumentationArticle';

export const GettingStarted = () => {
  return (
    <DocumentationArticle
      title="Getting Started with EchoRatings: First Steps After Signup"
      description="This guide helps you complete the basic setup in EchoRatings so you can start collecting and managing reviews without technical complexity."
      timeToComplete="5-10 minutes"
      requirements={[
        'Access to your EchoRatings account',
        'Basic business information',
      ]}
      sections={[
        {
          title: 'Section 1: Log In and Confirm Your Account',
          content: (
            <>
              <ol className="list-decimal list-inside space-y-2 mb-4">
                <li>
                  Open the login page using the link provided after signup.
                </li>
                <li>
                  Sign in with the email address and password you used to create
                  your account.
                </li>
                <li>
                  If email verification is required, check your inbox and confirm
                  your email address.
                </li>
              </ol>
              <p>
                After your first login, you should see the main dashboard. This is
                where you'll manage connections, reviews, and activity.
              </p>
            </>
          ),
        },
        {
          title: 'Section 2: Complete Your Business Profile',
          content: (
            <>
              <ol className="list-decimal list-inside space-y-2 mb-4">
                <li>Go to your account settings from the main navigation.</li>
                <li>
                  Review or enter your basic business information, such as business
                  name and contact details.
                </li>
                <li>Save your changes.</li>
              </ol>
              <p>
                Accurate business information helps ensure reviews and automations
                are linked to the correct business.
              </p>
            </>
          ),
        },
        {
          title: 'Section 3: Connect Your First Review Platform',
          content: (
            <>
              <p className="mb-4">
                EchoRatings works by connecting to the review platforms your
                customers already use.
              </p>
              <ol className="list-decimal list-inside space-y-2 mb-4">
                <li>Go to the integrations area from your settings.</li>
                <li>
                  Choose a review platform to connect. Google Reviews is a common
                  first step.
                </li>
                <li>
                  Follow the connection instructions for the selected platform.
                </li>
              </ol>
              <p>
                Each platform has its own guide. Use the relevant integration
                article to complete the connection.
              </p>
            </>
          ),
        },
        {
          title: 'Section 4: Review Your Dashboard Overview',
          content: (
            <>
              <p className="mb-4">After setup, return to the dashboard.</p>
              <p className="mb-2">Here you'll see:</p>
              <ul className="list-disc list-inside space-y-1 mb-4">
                <li>A summary of recent review activity</li>
                <li>The status of connected platforms</li>
                <li>Shortcuts to key actions</li>
              </ul>
              <p>
                This view is meant to help you understand where things stand at a
                glance. You do not need to configure anything here yet.
              </p>
            </>
          ),
        },
        {
          title: 'Section 5: What to Do Next',
          content: (
            <>
              <p className="mb-4">
                Once your initial setup is complete, you can:
              </p>
              <ol className="list-decimal list-inside space-y-2 mb-4">
                <li>Connect additional review platforms.</li>
                <li>Set up review automations.</li>
                <li>Start responding to incoming reviews.</li>
              </ol>
              <p>You can complete these steps at your own pace.</p>
            </>
          ),
        },
      ]}
      faq={[
        {
          question: 'Do reviews appear immediately after I connect a platform?',
          answer:
            'Reviews may take a short time to sync after the first connection.',
        },
        {
          question: 'Can I change my business information later?',
          answer:
            'Yes. You can update your business details at any time in your account settings.',
        },
        {
          question: 'Do I need to connect all platforms right away?',
          answer:
            'No. You can start with one platform and add others later.',
        },
      ]}
    />
  );
};
